import { inject, Injectable } from '@angular/core';
import {
  ApiDependencyProviderService,
  CollectionRequest,
  CollectionResponse,
  CreateResourceResponse,
  HttpParams,
} from '@web/common';
import { map, Observable } from 'rxjs';
import { blank, Guid } from '@web/utils';
import { AdminBannerDetailsResponse } from '../responses/admin-banner-details.response';
import { AdminBannerCollectionItemResponse } from '../responses/admin-banner-collection-item.response';
import { AdminBannerCreateRequest } from '../requests/admin-banner-create.request';
import { AdminBannerUpdateRequest } from '../requests/admin-banner-update.request';
import { BannerLocationType } from '../../../shared/enums/banner-location-type';

@Injectable({
  providedIn: 'root',
})
export class AdminBannerApiService {
  private readonly provider = inject(ApiDependencyProviderService);

  public fetchHeaderDetailsById(options: {
    id: Guid;
  }): Observable<AdminBannerDetailsResponse> {
    const { id } = options;
    const path = `admin/banner/${HttpParams.Id}`;
    /* prettier-ignore */
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, { [HttpParams.Id]: id });
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(AdminBannerDetailsResponse),
        ),
      );
  }

  public fetchDetailsByIdAndLocation(options: {
    id: Guid;
    location: BannerLocationType;
  }): Observable<AdminBannerDetailsResponse> {
    const { id, location } = options;
    const path = `admin/banner/${location}/${HttpParams.Id}`;
    /* prettier-ignore */
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, { [HttpParams.Id]: id });
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(AdminBannerDetailsResponse),
        ),
      );
  }

  public fetchHeaderCollection(options: {
    request: CollectionRequest;
  }): Observable<CollectionResponse<AdminBannerCollectionItemResponse>> {
    const { request } = options;
    const path = 'admin/banner';
    const url = this.provider.httpUrlFactory.withQueryParams(
      this.hostUrl(),
      path,
      this.provider.httpHelper.collectionRequestToQueryParams(request),
    );
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            AdminBannerCollectionItemResponse,
          ),
        ),
      );
  }

  public fetchCollectionByLocation(options: {
    request: CollectionRequest;
    location: BannerLocationType;
  }): Observable<CollectionResponse<AdminBannerCollectionItemResponse>> {
    const { request, location } = options;
    const path = `admin/banner/${location}`;
    const url = this.provider.httpUrlFactory.withQueryParams(
      this.hostUrl(),
      path,
      this.provider.httpHelper.collectionRequestToQueryParams(request),
    );
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            AdminBannerCollectionItemResponse,
          ),
        ),
      );
  }

  public removeHeaderById(options: { id: Guid }): Observable<void> {
    const { id } = options;
    const path = `admin/banner/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const context = this.provider.httpContextBuilder.build();

    return this.provider.http.delete(url, { context }).pipe(blank());
  }

  public removeByIdAndLocation(options: {
    id: Guid;
    location: BannerLocationType;
  }): Observable<void> {
    const { id, location } = options;
    const path = `admin/banner/${location}/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const context = this.provider.httpContextBuilder.build();

    return this.provider.http.delete(url, { context }).pipe(blank());
  }

  public createHeader(options: {
    request: AdminBannerCreateRequest;
  }): Observable<CreateResourceResponse> {
    const { request } = options;
    const path = 'admin/banner';
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();
    const body = this.provider.httpHelper.requestToBody(
      request,
      AdminBannerCreateRequest,
    );

    return this.provider.http
      .post(url, body, { context })
      .pipe(
        map(this.provider.httpHelper.bodyToResponse(CreateResourceResponse)),
      );
  }

  public createByLocation(options: {
    request: AdminBannerCreateRequest;
    location: BannerLocationType;
  }): Observable<CreateResourceResponse> {
    const { request, location } = options;
    const path = `admin/banner/${location}`;
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();
    const body = this.provider.httpHelper.requestToBody(
      request,
      AdminBannerCreateRequest,
    );

    return this.provider.http
      .post(url, body, { context })
      .pipe(
        map(this.provider.httpHelper.bodyToResponse(CreateResourceResponse)),
      );
  }

  public updateHeaderById(options: {
    id: Guid;
    request: AdminBannerUpdateRequest;
  }): Observable<void> {
    const { request, id } = options;
    const path = `admin/banner/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();
    const body = this.provider.httpHelper.requestToBody(
      request,
      AdminBannerUpdateRequest,
    );

    return this.provider.http.put(url, body, { context }).pipe(blank());
  }

  public updateByIdAndLocation(options: {
    id: Guid;
    request: AdminBannerUpdateRequest;
    location: BannerLocationType;
  }): Observable<void> {
    const { request, id, location } = options;
    const path = `admin/banner/${location}/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();
    const body = this.provider.httpHelper.requestToBody(
      request,
      AdminBannerUpdateRequest,
    );

    return this.provider.http.put(url, body, { context }).pipe(blank());
  }

  private hostUrl(): string {
    return this.provider.environment.ApiHostUrl.Shop;
  }
}
